import { useCallback, useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Link } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { DataTable, type Column } from "@/components/DataTable";
import {
  EscalaIntegracaoDetalheModal,
  type DetalheExecucao,
} from "@/components/escala/EscalaIntegracaoDetalheModal";
import { StatusExecucaoChip } from "@/components/escala/StatusExecucaoChip";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import {
  formatDataHoraBR,
  listExecucoesIntegracao,
  STATUS_EXECUCAO_LABEL,
  type ExecucaoIntegracao,
} from "@/api/escala.service";
import {
  formatarDuracao,
  mensagemErroExecucao,
  metricasExecucao,
} from "@/utils/escalaIntegracao";

// Número que abre o detalhe quando há o que detalhar; zero é só zero.
function NumeroClicavel({
  valor,
  onClick,
}: {
  valor: number;
  onClick: () => void;
}) {
  if (valor === 0) return <>{valor}</>;
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      underline="always"
      onClick={onClick}
    >
      {valor}
    </Link>
  );
}

function colunas(
  abrir: (d: DetalheExecucao) => void,
): Column<ExecucaoIntegracao>[] {
  const numero =
    (
      tipo: DetalheExecucao["tipo"],
      valor: (e: ExecucaoIntegracao) => number,
    ): Column<ExecucaoIntegracao>["render"] =>
    (e) => (
      <NumeroClicavel
        valor={valor(e)}
        onClick={() => abrir({ execucao: e, tipo })}
      />
    );
  return [
    {
      key: "iniciada_em",
      label: "Início",
      render: (e) => formatDataHoraBR(e.iniciada_em),
      exportValue: (e) => formatDataHoraBR(e.iniciada_em),
    },
    {
      key: "duracao",
      label: "Duração",
      render: (e) => formatarDuracao(e.iniciada_em, e.concluida_em),
      exportValue: (e) => formatarDuracao(e.iniciada_em, e.concluida_em),
    },
    {
      key: "status",
      label: "Status",
      render: (e) => <StatusExecucaoChip status={e.status} />,
      exportValue: (e) => STATUS_EXECUCAO_LABEL[e.status],
    },
    {
      key: "inseridos",
      label: "Inseridos",
      render: numero("inseridos", (e) => metricasExecucao(e).inseridos),
      exportValue: (e) => metricasExecucao(e).inseridos,
    },
    {
      key: "atualizados",
      label: "Atualizados",
      render: numero("atualizados", (e) => metricasExecucao(e).atualizados),
      exportValue: (e) => metricasExecucao(e).atualizados,
    },
    {
      key: "erros_total",
      label: "Erros",
      render: numero("erros", (e) => e.erros_total),
      exportValue: (e) => e.erros_total,
    },
    {
      key: "mensagem",
      label: "Mensagem",
      render: mensagemErroExecucao,
      exportValue: mensagemErroExecucao,
    },
  ];
}

// Histórico das sincronizações com o sistema externo. `recarregarEm` muda quando uma
// sincronização termina, para a lista acompanhar sem o usuário clicar em Atualizar.
export function EscalaIntegracaoHistorico({
  recarregarEm = 0,
}: {
  recarregarEm?: number;
}) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();

  const [execucoes, setExecucoes] = useState<ExecucaoIntegracao[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detalhe, setDetalhe] = useState<DetalheExecucao | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setExecucoes(await listExecucoesIntegracao(clientId));
    } catch (e: any) {
      notify(
        e?.message || "Erro ao carregar o histórico de sincronizações",
        "error",
      );
    } finally {
      setCarregando(false);
    }
  }, [clientId, notify]);

  useEffect(() => {
    carregar();
  }, [carregar, recarregarEm]);

  if (!execucoes) return carregando ? <CircularProgress size={24} /> : null;
  if (execucoes.length === 0)
    return <Alert severity="info">Nenhuma sincronização feita ainda.</Alert>;

  return (
    <>
      <DataTable
        data={execucoes.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        )}
        columns={colunas(setDetalhe)}
        total={execucoes.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        exportToExcel
        excelFileName="historico_sincronizacoes"
        toolbar={
          <Button
            size="small"
            variant="outlined"
            startIcon={
              carregando ? (
                <CircularProgress size={16} />
              ) : (
                <Refresh fontSize="small" />
              )
            }
            onClick={carregar}
            disabled={carregando}
          >
            Atualizar
          </Button>
        }
      />
      <EscalaIntegracaoDetalheModal
        detalhe={detalhe}
        onClose={() => setDetalhe(null)}
      />
    </>
  );
}
